<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("account.menu.records") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button size="md" type="neutral" @click="handleCreate()">
            <i class="fa fa-plus"></i> {{ $t("add") }}
          </base-button>
          <base-button size="md" type="info" @click="handleBatchCreate()">
            <i class="fa fa-list"></i> {{ $t("account.batch_create") }}
          </base-button>
        </div>
      </div>
    </base-header>
    <div class="container-fluid mt--6">
      <div>
        <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
          <template slot="header">
            <div class="row">
              <div class="col-md-3">
                <base-input>
                  <el-date-picker
                    v-model="filterDateRange"
                    type="daterange"
                    range-separator="-"
                    :start-placeholder="$t('account.date_from')"
                    :end-placeholder="$t('account.date_to')"
                    format="yyyy-MM-dd"
                    value-format="yyyy-MM-dd"
                    @change="handleSearch"
                  >
                  </el-date-picker>
                </base-input>
              </div>
              <div class="col-md-2">
                <base-input>
                  <el-select v-model="filterType" :placeholder="$t('account.type')" @change="handleSearch" clearable>
                    <el-option :label="$t('account.all')" :value="0"></el-option>
                    <el-option :label="$t('account.income')" :value="1"></el-option>
                    <el-option :label="$t('account.expense')" :value="2"></el-option>
                  </el-select>
                </base-input>
              </div>
              <div class="col-md-2">
                <base-input>
                  <el-select v-model="filterItemId" :placeholder="$t('account.category')" @change="handleSearch" clearable filterable popper-class="account-tree-popper">
                    <el-option :label="$t('account.all')" :value="0"></el-option>
                    <el-option
                      v-for="opt in filterOptions"
                      :key="opt.value"
                      :label="opt.label"
                      :value="opt.value"
                    >
                      <span :style="{ paddingLeft: opt.depth * 14 + 'px' }">{{ opt.label }}</span>
                    </el-option>
                  </el-select>
                </base-input>
              </div>
              <div class="col-md-1">
                <base-button type="primary" @click="handleSearch">
                  <i class="fa fa-search"></i>
                </base-button>
              </div>
            </div>
          </template>
          <div>
            <el-table
              :data="records"
              row-key="id"
              header-row-class-name="thead-light"
              v-loading="loading"
            >
              <el-table-column :label="$t('account.transaction_date')" min-width="120">
                <div slot-scope="{ row }">
                  {{ formatDate(row.transaction_date) }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.type')" min-width="100" align="center">
                <div slot-scope="{ row }">
                  <badge :type="row.type === 1 ? 'success' : 'danger'">
                    {{ row.type === 1 ? $t('account.income') : $t('account.expense') }}
                  </badge>
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.category')" min-width="200">
                <div slot-scope="{ row }">
                  {{ row.account_item_path || row.account_item_name }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.amount')" min-width="120" align="right">
                <div slot-scope="{ row }" :class="row.type === 1 ? 'text-success' : 'text-danger'">
                  {{ row.type === 1 ? '+' : '-' }}{{ formatNumber(row.amount) }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.description')" min-width="200">
                <div slot-scope="{ row }">
                  {{ row.description || '-' }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.reference_no')" min-width="120">
                <div slot-scope="{ row }">
                  {{ row.reference_no || '-' }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.created_by')" min-width="120">
                <div slot-scope="{ row }">
                  {{ row.user_name || '-' }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('actions')" min-width="140" align="center">
                <div slot-scope="{ row }" class="d-flex justify-content-center">
                  <base-button
                    @click.native="handleEdit(row)"
                    class="edit"
                    type="warning"
                    size="sm"
                    icon
                  >
                    <i class="text-white fa fa-edit"></i>
                  </base-button>
                  <base-button
                    @click.native="handleAttachments(row)"
                    class="attach"
                    type="info"
                    size="sm"
                    icon
                  >
                    <i class="text-white fa fa-paperclip"></i>
                  </base-button>
                  <base-button
                    @click.native="handleDelete(row)"
                    class="remove btn-link"
                    type="danger"
                    size="sm"
                    icon
                  >
                    <i class="text-white fa fa-trash"></i>
                  </base-button>
                </div>
              </el-table-column>
            </el-table>
          </div>
          <div
            slot="footer"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
            v-if="records.length != 0"
          >
            <div class="">
              <p class="card-category">
                {{ $t("data") }} {{ fromRecord }} {{ $t("to") }} {{ toRecord }}
                {{ $t("of") }} {{ pagination.total }}
              </p>
            </div>
            <base-pagination
              class="pagination-no-border"
              v-model="pagination.page"
              :per-page="pagination.per_page"
              :total="pagination.total"
              @input="handlePageChange"
            >
            </base-pagination>
          </div>
        </card>
      </div>
    </div>

    <!-- BATCH CREATE MODAL -->
    <modal :show.sync="showBatchModal" modal-classes="modal-dialog-centered modal-xl modal-secondary" @close="showBatchModal = false">
      <h5 slot="header" class="modal-title">{{ $t("account.batch_create") }}</h5>

      <form class="new-event--form" @submit.prevent="">
        <div class="form-group">
          <label>{{ $t("account.transaction_date") }} *</label>
          <el-date-picker
            v-model="batchDate"
            type="date"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            class="w-100"
          />
        </div>

        <el-table :data="batchRows" row-key="idx" header-row-class-name="thead-light" size="small">
          <el-table-column :label="$t('account.type')" min-width="120">
            <template slot-scope="{ row }">
              <el-select v-model="row.type" size="small" @change="row.account_item_id = null">
                <el-option :label="$t('account.income')" :value="1"></el-option>
                <el-option :label="$t('account.expense')" :value="2"></el-option>
              </el-select>
            </template>
          </el-table-column>

          <el-table-column :label="$t('account.category')" min-width="240">
            <template slot-scope="{ row }">
              <el-select v-model="row.account_item_id" size="small" filterable class="w-100" :placeholder="$t('account.search_category')" popper-class="account-tree-popper">
                <el-option
                  v-for="opt in optionsForType(row.type)"
                  :key="opt.value"
                  :label="opt.label"
                  :value="opt.value"
                >
                  <span :style="{ paddingLeft: opt.depth * 14 + 'px' }">{{ opt.label }}</span>
                </el-option>
              </el-select>
            </template>
          </el-table-column>

          <el-table-column :label="$t('account.amount')" min-width="120">
            <template slot-scope="{ row }">
              <el-input v-model="row.amount" type="number" size="small" min="0" step="0.01"></el-input>
            </template>
          </el-table-column>

          <el-table-column :label="$t('account.description')" min-width="140">
            <template slot-scope="{ row }">
              <el-input v-model="row.description" size="small"></el-input>
            </template>
          </el-table-column>

          <el-table-column :label="$t('account.reference_no')" min-width="120">
            <template slot-scope="{ row }">
              <el-input v-model="row.reference_no" size="small"></el-input>
            </template>
          </el-table-column>

          <el-table-column :label="$t('account.attach_file')" width="80" align="center">
            <template slot-scope="{ row }">
              <base-button type="info" size="sm" icon @click.native="handleBatchRowAttachments(row)">
                <i class="fa fa-paperclip"></i>
                <span v-if="row.files && row.files.length > 0" class="badge badge-light badge-pill ml-1" style="font-size:10px;">{{ row.files.length }}</span>
              </base-button>
            </template>
          </el-table-column>

          <el-table-column width="70" align="center">
            <template slot-scope="{ $index }">
              <base-button type="danger" size="sm" icon @click.native="batchRows.splice($index, 1)">
                <i class="fa fa-trash"></i>
              </base-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="mt-3">
          <base-button type="primary" size="sm" @click="addBatchRow()">
            <i class="fa fa-plus"></i> {{ $t("account.add_row") }}
          </base-button>
          <base-button type="default" size="sm" @click="duplicateBatchRow()">
            <i class="fa fa-copy"></i> {{ $t("account.duplicate_row") }}
          </base-button>
        </div>

        <div class="mt-3 d-flex justify-content-between">
          <div>
            <span class="text-success mr-3">{{ $t("account.income") }}: {{ batchTotalIncome }}</span>
            <span class="text-danger">{{ $t("account.expense") }}: {{ batchTotalExpense }}</span>
          </div>
          <span>{{ $t("account.total_rows") }}: {{ batchRows.length }}</span>
        </div>
      </form>

      <template slot="footer">
        <button class="btn btn-md btn-primary" @click="handleBatchSave()" :disabled="batchRows.length === 0">
          {{ $t("save") }}
        </button>
        <button class="btn btn-md btn-link ml-auto" @click="showBatchModal = false">
          {{ $t("close") }}
        </button>
      </template>
    </modal>

    <!-- Create/Edit Modal -->
    <modal :show.sync="showModal" modal-classes="modal-dialog-centered modal-lg modal-secondary">
      <h5 slot="header" class="modal-title">
        {{ isEdit ? $t('account.edit_record') : $t('account.create_record') }}
      </h5>
      <form class="new-event--form" @submit.prevent="">
        <div class="form-group">
          <label class="form-control-label">{{ $t('account.type') }} *</label>
          <el-select v-model="formData.type" class="w-100" @change="handleTypeChange" :disabled="isEdit">
            <el-option :label="$t('account.income')" :value="1"></el-option>
            <el-option :label="$t('account.expense')" :value="2"></el-option>
          </el-select>
        </div>

        <div class="form-group">
          <label class="form-control-label">{{ $t('account.transaction_date') }} *</label>
          <el-date-picker
            v-model="formData.transaction_date"
            type="date"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            class="w-100"
          >
          </el-date-picker>
        </div>

        <div class="form-group">
          <label class="form-control-label">{{ $t('account.category') }} *</label>
          <el-select v-model="formData.account_item_id" class="w-100 account-record-category" filterable :placeholder="$t('account.search_category')" popper-class="account-tree-popper">
            <el-option
              v-for="opt in filteredItems"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            >
              <span :style="{ paddingLeft: opt.depth * 14 + 'px' }">{{ opt.label }}</span>
            </el-option>
          </el-select>
        </div>

        <base-input :label="$t('account.amount') + ' *'" v-model="formData.amount" type="number" min="0" step="0.01">
        </base-input>

        <base-input :label="$t('account.description')" v-model="formData.description">
        </base-input>

        <base-input :label="$t('account.reference_no')" v-model="formData.reference_no">
        </base-input>
      </form>

      <template slot="footer">
        <button type="submit" class="btn btn-md btn-primary" @click="handleSave()">
          {{ $t("save") }}
        </button>
        <button type="button" class="btn btn-md btn-link ml-auto" @click="showModal = false">
          {{ $t("close") }}
        </button>
      </template>
    </modal>

    <!-- Attachments Modal (existing record) -->
    <modal :show.sync="showAttachModal" modal-classes="modal-dialog-centered modal-lg modal-secondary" @close="onAttachModalClose">
      <h5 slot="header" class="modal-title">{{ $t("account.attachments") }}</h5>

      <div>
        <!-- Upload section -->
        <div class="mb-3">
          <label class="form-control-label d-block">{{ $t("account.select_files") }}</label>
          <div class="d-flex align-items-center">
            <input
              ref="attachFileInput"
              type="file"
              multiple
              class="form-control-file"
              style="flex:1;"
              @change="onAttachFilesSelected"
            />
            <base-button
              type="primary"
              size="sm"
              class="ml-2"
              :disabled="attachPendingFiles.length === 0 || attachUploading"
              @click="uploadPendingFiles"
            >
              <i class="fa fa-upload"></i> {{ $t("account.upload") }}
            </base-button>
          </div>
          <div v-if="attachPendingFiles.length > 0" class="mt-2">
            <small class="text-muted">{{ $t("account.files_pending") }}: {{ attachPendingFiles.length }}</small>
            <ul class="list-unstyled mb-0 mt-1">
              <li v-for="(f, i) in attachPendingFiles" :key="i" class="small">
                <i class="fa fa-file mr-1"></i>{{ f.name }}
                <span v-if="f.size > 10 * 1024 * 1024" class="text-danger ml-1">({{ $t("account.file_too_large") }})</span>
              </li>
            </ul>
          </div>
          <div v-if="attachUploading" class="mt-2 text-info small">
            <i class="fa fa-spinner fa-spin mr-1"></i>{{ attachUploadProgress }}
          </div>
        </div>

        <!-- Existing files -->
        <div>
          <label class="form-control-label">{{ $t("account.uploaded_files") }}</label>
          <div v-if="attachDocsLoading" class="text-center py-3">
            <i class="fa fa-spinner fa-spin"></i>
          </div>
          <div v-else-if="attachDocs.length === 0" class="text-muted small py-2">
            {{ $t("account.no_attachments") }}
          </div>
          <table v-else class="table table-sm table-bordered">
            <thead class="thead-light">
              <tr>
                <th>{{ $t("account.item_name") }}</th>
                <th>{{ $t("account.transaction_date") }}</th>
                <th>{{ $t("account.created_by") }}</th>
                <th class="text-center">{{ $t("actions") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="doc in attachDocs" :key="doc.id">
                <td>{{ doc.filename }}</td>
                <td>{{ formatDate(doc.uploaded_at) }}</td>
                <td>{{ doc.uploaded_by_name }}</td>
                <td class="text-center">
                  <base-button
                    v-if="isImageFile(doc.filename)"
                    type="info"
                    size="sm"
                    @click="previewDoc(doc)"
                  >
                    <i class="fa fa-eye"></i> {{ $t("account.view") }}
                  </base-button>
                  <base-button type="primary" size="sm" class="ml-1" @click="downloadDoc(doc)">
                    <i class="fa fa-download"></i> {{ $t("account.download") }}
                  </base-button>
                  <base-button type="danger" size="sm" class="ml-1" @click="deleteDoc(doc)">
                    <i class="fa fa-trash"></i>
                  </base-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <template slot="footer">
        <button type="button" class="btn btn-md btn-link ml-auto" @click="showAttachModal = false">
          {{ $t("close") }}
        </button>
      </template>
    </modal>

    <!-- Image Preview Modal -->
    <modal :show.sync="showImagePreviewModal" modal-classes="modal-dialog-centered modal-lg modal-secondary" @close="onImagePreviewClose">
      <h5 slot="header" class="modal-title">
        <i class="fa fa-image mr-1"></i>{{ imagePreviewName }}
      </h5>
      <div class="text-center">
        <div v-if="imagePreviewLoading" class="py-5">
          <i class="fa fa-spinner fa-spin fa-2x"></i>
        </div>
        <img
          v-else-if="imagePreviewUrl"
          :src="imagePreviewUrl"
          :alt="imagePreviewName"
          style="max-width:100%; max-height:75vh; object-fit:contain;"
        />
      </div>
      <template slot="footer">
        <button
          v-if="imagePreviewUrl"
          type="button"
          class="btn btn-md btn-primary"
          @click="openImageInNewTab"
        >
          <i class="fa fa-external-link-alt"></i> {{ $t("account.download") }}
        </button>
        <button type="button" class="btn btn-md btn-link ml-auto" @click="showImagePreviewModal = false">
          {{ $t("close") }}
        </button>
      </template>
    </modal>

    <!-- Batch Row Staged Files Modal -->
    <modal :show.sync="showBatchAttachModal" modal-classes="modal-dialog-centered modal-secondary" @close="showBatchAttachModal = false">
      <h5 slot="header" class="modal-title">{{ $t("account.attach_file") }}</h5>

      <div>
        <div class="mb-3">
          <label class="form-control-label d-block">{{ $t("account.select_files") }}</label>
          <input
            ref="batchAttachFileInput"
            type="file"
            multiple
            class="form-control-file"
            @change="onBatchAttachFilesSelected"
          />
        </div>
        <div v-if="batchAttachRow && batchAttachRow.files && batchAttachRow.files.length > 0">
          <label class="form-control-label">{{ $t("account.files_pending") }}</label>
          <ul class="list-group">
            <li
              v-for="(f, i) in batchAttachRow.files"
              :key="i"
              class="list-group-item d-flex justify-content-between align-items-center py-1 px-2"
            >
              <span class="small"><i class="fa fa-file mr-1"></i>{{ f.name }}</span>
              <base-button type="danger" size="sm" icon @click.native="removeBatchFile(i)">
                <i class="fa fa-times"></i>
              </base-button>
            </li>
          </ul>
        </div>
        <div v-else class="text-muted small">{{ $t("account.no_attachments") }}</div>
      </div>

      <template slot="footer">
        <button type="button" class="btn btn-md btn-primary" @click="showBatchAttachModal = false">
          {{ $t("confirm") }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, DatePicker } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination, Badge } from "@/components";
import swal from "sweetalert2";
import { mapActions, mapGetters } from "vuex";

import { categoryOptions } from "@/utils/accountTree";
export default {
  components: {
    BasePagination,
    RouteBreadCrumb,
    Badge,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
  },
  computed: {
    ...mapGetters({
      records: "Account/getRecords",
      recordsPagination: "Account/getRecordsPagination",
      items: "Account/getItems",
    }),
    // ONE dropdown, every active category of the record's type in tree order,
    // labelled "A > B > C" (Element filters on the label, so any level matches).
    filteredItems() {
      return categoryOptions(this.items, this.formData.type);
    },
    filterOptions() {
      return categoryOptions(this.items, 0);
    },
    optionsByType() {
      return { 1: categoryOptions(this.items, 1), 2: categoryOptions(this.items, 2) };
    },
    batchTotalIncome() {
      return this.batchRows
        .filter(r => r.type === 1 && r.amount)
        .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0)
        .toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    batchTotalExpense() {
      return this.batchRows
        .filter(r => r.type === 2 && r.amount)
        .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0)
        .toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fromRecord() {
      return (this.pagination.page - 1) * this.pagination.per_page + 1;
    },
    toRecord() {
      const to = this.pagination.page * this.pagination.per_page;
      return to > this.pagination.total ? this.pagination.total : to;
    },
  },
  data() {
    return {
      loading: false,
      showModal: false,
      isEdit: false,
      showBatchModal: false,
      batchRows: [],
      batchDate: '',
      filterDateRange: [],
      filterType: 0,
      filterItemId: 0,
      pagination: {
        page: 1,
        per_page: 50,
        total: 0,
      },
      formData: {
        id: 0,
        account_item_id: null,
        type: 2,
        amount: "",
        description: "",
        reference_no: "",
        transaction_date: "",
      },
      // Attachments modal (existing records)
      showAttachModal: false,
      attachRecordId: null,
      attachDocs: [],
      attachDocsLoading: false,
      attachPendingFiles: [],
      attachUploading: false,
      attachUploadProgress: "",
      // Batch row staged-files modal
      showBatchAttachModal: false,
      batchAttachRow: null,
      // Image preview modal
      showImagePreviewModal: false,
      imagePreviewUrl: "",
      imagePreviewName: "",
      imagePreviewLoading: false,
    };
  },
  watch: {
    recordsPagination: {
      handler(val) {
        this.pagination = { ...val };
      },
      immediate: true,
    },
  },
  mounted() {
    this.loadItems();
    this.loadData();
  },
  methods: {
    ...mapActions({
      listItems: "Account/listItems",
      listRecords: "Account/listRecords",
      createRecord: "Account/createRecord",
      updateRecord: "Account/updateRecord",
      deleteRecord: "Account/deleteRecord",
      createRecordBatch: "Account/createRecordBatch",
      listRecordDocuments: "Account/listRecordDocuments",
      uploadRecordDocument: "Account/uploadRecordDocument",
      getRecordDocumentUrl: "Account/getRecordDocumentUrl",
      deleteRecordDocument: "Account/deleteRecordDocument",
    }),
    optionsForType(type) {
      return this.optionsByType[type] || [];
    },
    async loadItems() {
      try {
        await this.listItems({ is_active: "true" });
      } catch (error) {
        console.log(error);
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          per_page: this.pagination.per_page,
        };
        if (this.filterDateRange && this.filterDateRange.length === 2) {
          params.time_from = this.filterDateRange[0];
          params.time_to = this.filterDateRange[1];
        }
        if (this.filterType > 0) params.type = this.filterType;
        if (this.filterItemId > 0) params.account_item_id = this.filterItemId;
        await this.listRecords(params);
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.pagination.page = 1;
      this.loadData();
    },
    handlePageChange() {
      this.loadData();
    },
    handleTypeChange() {
      this.formData.account_item_id = null;
    },
    handleCreate() {
      this.isEdit = false;
      const today = new Date().toISOString().split('T')[0];
      this.formData = {
        id: 0,
        account_item_id: null,
        type: 2,
        amount: "",
        description: "",
        reference_no: "",
        transaction_date: today,
      };
      this.showModal = true;
    },
    handleEdit(row) {
      this.isEdit = true;
      this.formData = {
        id: row.id,
        account_item_id: row.account_item_id,
        type: row.type,
        amount: row.amount,
        description: row.description || "",
        reference_no: row.reference_no || "",
        transaction_date: row.transaction_date ? row.transaction_date.split('T')[0] : "",
      };
      this.showModal = true;
    },
    handleSave() {
      var self = this;
      if (!this.formData.account_item_id || !this.formData.amount || !this.formData.transaction_date) {
        swal.fire({
          title: this.$t("account.error_required_fields"),
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false,
        });
        return;
      }

      const payload = {
        account_item_id: this.formData.account_item_id,
        type: this.formData.type,
        amount: parseFloat(this.formData.amount),
        description: this.formData.description,
        reference_no: this.formData.reference_no,
        transaction_date: this.formData.transaction_date,
      };

      if (this.isEdit) {
        this.updateRecord({ id: this.formData.id, payload })
          .then(function () {
            self.showModal = false;
            self.loadData();
            swal.fire({
              title: self.$t("edit_success"),
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false,
            });
          })
          .catch(function (err) {
            var errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
            swal.fire({
              title: self.$t("error"),
              text: errorMsg,
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false,
            });
          });
      } else {
        this.createRecord(payload)
          .then(function () {
            self.showModal = false;
            self.loadData();
            swal.fire({
              title: self.$t("create_success"),
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false,
            });
          })
          .catch(function (err) {
            var errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
            swal.fire({
              title: self.$t("error"),
              text: errorMsg,
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false,
            });
          });
      }
    },
    handleDelete(row) {
      var self = this;
      swal
        .fire({
          title: this.$t("delete_confirm"),
          text: this.$t("delete_confirm_question"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonClass: "btn btn-success btn-fill",
          cancelButtonClass: "btn btn-danger btn-fill",
          confirmButtonText: this.$t("confirm"),
          cancelButtonText: this.$t("cancel"),
          buttonsStyling: false,
        })
        .then(function (result) {
          if (result.value) {
            self.deleteRecord(row.id)
              .then(function () {
                self.loadData();
                swal.fire({
                  title: self.$t("delete_success"),
                  icon: "success",
                  confirmButtonClass: "btn btn-success btn-fill",
                  buttonsStyling: false,
                });
              })
              .catch(function (err) {
                var errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
                swal.fire({
                  title: self.$t("error"),
                  text: errorMsg,
                  icon: "error",
                  confirmButtonClass: "btn btn-danger btn-fill",
                  buttonsStyling: false,
                });
              });
          }
        });
    },
    // ----------------------------------------------------------------
    // Attachments modal (existing records)
    // ----------------------------------------------------------------
    async handleAttachments(row) {
      this.attachRecordId = row.id;
      this.attachDocs = [];
      this.attachPendingFiles = [];
      this.attachUploading = false;
      this.attachUploadProgress = "";
      this.showAttachModal = true;
      await this.loadAttachDocs();
    },
    onAttachModalClose() {
      this.attachPendingFiles = [];
      if (this.$refs.attachFileInput) {
        this.$refs.attachFileInput.value = "";
      }
    },
    async loadAttachDocs() {
      this.attachDocsLoading = true;
      try {
        const resp = await this.listRecordDocuments(this.attachRecordId);
        this.attachDocs = (resp.data && resp.data.Data) ? resp.data.Data : [];
      } catch (err) {
        console.log(err);
        this.attachDocs = [];
      } finally {
        this.attachDocsLoading = false;
      }
    },
    onAttachFilesSelected(event) {
      this.attachPendingFiles = Array.from(event.target.files || []);
    },
    async uploadPendingFiles() {
      const files = this.attachPendingFiles;
      // Client-side size check
      for (let i = 0; i < files.length; i++) {
        if (files[i].size > 10 * 1024 * 1024) {
          swal.fire({
            title: this.$t("error"),
            text: `${files[i].name}: ${this.$t("account.file_too_large")}`,
            icon: "error",
            confirmButtonClass: "btn btn-danger btn-fill",
            buttonsStyling: false,
          });
          return;
        }
      }
      this.attachUploading = true;
      for (let i = 0; i < files.length; i++) {
        this.attachUploadProgress = `${this.$t("account.uploading")} ${i + 1}/${files.length}`;
        const formData = new FormData();
        formData.append("file", files[i]);
        try {
          await this.uploadRecordDocument({ recordId: this.attachRecordId, formData });
        } catch (err) {
          const errorMsg = (err.response && err.response.data && err.response.data.message) || "Upload failed";
          swal.fire({
            title: this.$t("error"),
            text: `${files[i].name}: ${errorMsg}`,
            icon: "error",
            confirmButtonClass: "btn btn-danger btn-fill",
            buttonsStyling: false,
          });
          this.attachUploading = false;
          this.attachUploadProgress = "";
          return;
        }
      }
      this.attachUploading = false;
      this.attachUploadProgress = "";
      this.attachPendingFiles = [];
      if (this.$refs.attachFileInput) {
        this.$refs.attachFileInput.value = "";
      }
      await this.loadAttachDocs();
    },
    isImageFile(filename) {
      if (!filename) return false;
      const ext = filename.toLowerCase().split(".").pop();
      return ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].indexOf(ext) >= 0;
    },
    async previewDoc(doc) {
      this.imagePreviewName = doc.filename;
      this.imagePreviewUrl = "";
      this.imagePreviewLoading = true;
      this.showImagePreviewModal = true;
      try {
        const resp = await this.getRecordDocumentUrl({ recordId: this.attachRecordId, docId: doc.id });
        const data = resp.data && resp.data.Data && resp.data.Data[0];
        if (data && data.url) {
          this.imagePreviewUrl = data.url;
        }
      } catch (err) {
        const errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
        swal.fire({
          title: this.$t("error"),
          text: errorMsg,
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false,
        });
        this.showImagePreviewModal = false;
      } finally {
        this.imagePreviewLoading = false;
      }
    },
    onImagePreviewClose() {
      this.imagePreviewUrl = "";
      this.imagePreviewName = "";
    },
    openImageInNewTab() {
      if (this.imagePreviewUrl) {
        window.open(this.imagePreviewUrl, "_blank");
      }
    },
    async downloadDoc(doc) {
      try {
        const resp = await this.getRecordDocumentUrl({ recordId: this.attachRecordId, docId: doc.id });
        const data = resp.data && resp.data.Data && resp.data.Data[0];
        if (data && data.url) {
          window.open(data.url, "_blank");
        }
      } catch (err) {
        const errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
        swal.fire({
          title: this.$t("error"),
          text: errorMsg,
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false,
        });
      }
    },
    async deleteDoc(doc) {
      const result = await swal.fire({
        title: this.$t("delete_confirm"),
        text: doc.filename,
        icon: "warning",
        showCancelButton: true,
        confirmButtonClass: "btn btn-success btn-fill",
        cancelButtonClass: "btn btn-danger btn-fill",
        confirmButtonText: this.$t("confirm"),
        cancelButtonText: this.$t("cancel"),
        buttonsStyling: false,
      });
      if (!result.value) return;
      try {
        await this.deleteRecordDocument({ recordId: this.attachRecordId, docId: doc.id });
        await this.loadAttachDocs();
      } catch (err) {
        const errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
        swal.fire({
          title: this.$t("error"),
          text: errorMsg,
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false,
        });
      }
    },
    // ----------------------------------------------------------------
    // Batch helpers
    // ----------------------------------------------------------------
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("th-TH", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
    formatNumber(num) {
      if (!num) return "0.00";
      return parseFloat(num).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    handleBatchCreate() {
      const today = new Date().toISOString().split('T')[0];
      this.batchDate = today;
      this.batchRows = [
        { idx: 1, type: 2, account_item_id: null, amount: "", description: "", reference_no: "", files: [] }
      ];
      this.showBatchModal = true;
    },
    addBatchRow() {
      if (this.batchRows.length >= 100) {
        swal.fire({ title: this.$t("error"), text: "Maximum 100 rows per batch", icon: "warning" });
        return;
      }
      const maxIdx = this.batchRows.length > 0 ? Math.max(...this.batchRows.map(r => r.idx)) : 0;
      this.batchRows.push({
        idx: maxIdx + 1,
        type: 2,
        account_item_id: null,
        amount: "",
        description: "",
        reference_no: "",
        files: [],
      });
    },
    duplicateBatchRow() {
      if (this.batchRows.length === 0) return;
      if (this.batchRows.length >= 100) {
        swal.fire({ title: this.$t("error"), text: "Maximum 100 rows per batch", icon: "warning" });
        return;
      }
      const last = this.batchRows[this.batchRows.length - 1];
      const maxIdx = Math.max(...this.batchRows.map(r => r.idx));
      this.batchRows.push({
        idx: maxIdx + 1,
        type: last.type,
        account_item_id: last.account_item_id,
        amount: "",
        description: "",
        reference_no: "",
        files: [],
      });
    },
    async handleBatchSave() {
      if (!this.batchDate) {
        swal.fire({ title: this.$t("account.error_required_fields"), icon: "error" });
        return;
      }

      for (let i = 0; i < this.batchRows.length; i++) {
        const row = this.batchRows[i];
        if (!row.account_item_id || !row.amount) {
          swal.fire({ title: `Row ${i + 1}: ` + this.$t("account.error_required_fields"), icon: "error" });
          return;
        }
      }

      const records = this.batchRows.map(row => ({
        account_item_id: row.account_item_id,
        type: row.type,
        amount: parseFloat(row.amount),
        description: row.description || "",
        reference_no: row.reference_no || "",
        transaction_date: this.batchDate,
      }));

      let batchResp;
      try {
        batchResp = await this.createRecordBatch({ records });
      } catch (err) {
        const errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
        swal.fire({ title: this.$t("error"), text: errorMsg, icon: "error" });
        return;
      }

      // Upload staged files for rows that have them.
      // Backend returns Data: [{ total_requested, total_created, record_ids: [id1, id2, ...] }]
      // — record_ids is index-aligned with the request order.
      const rowsWithFiles = this.batchRows.filter(r => r.files && r.files.length > 0);
      if (rowsWithFiles.length > 0) {
        const batchResult = batchResp.data && batchResp.data.Data && batchResp.data.Data[0];
        const recordIds = (batchResult && batchResult.record_ids) ? batchResult.record_ids : [];
        swal.fire({
          title: this.$t("account.uploading"),
          allowOutsideClick: false,
          didOpen: () => { swal.showLoading(); },
        });
        for (let i = 0; i < this.batchRows.length; i++) {
          const row = this.batchRows[i];
          if (!row.files || row.files.length === 0) continue;
          const newId = recordIds[i];
          if (!newId) continue;
          for (let j = 0; j < row.files.length; j++) {
            const formData = new FormData();
            formData.append("file", row.files[j]);
            try {
              await this.uploadRecordDocument({ recordId: newId, formData });
            } catch (err) {
              console.log("Attachment upload error", err);
            }
          }
        }
        swal.close();
      }

      this.showBatchModal = false;
      this.loadData();
      swal.fire({ title: this.$t("create_success"), icon: "success" });
    },
    // Batch row attachment staging
    handleBatchRowAttachments(row) {
      this.batchAttachRow = row;
      this.showBatchAttachModal = true;
    },
    onBatchAttachFilesSelected(event) {
      const newFiles = Array.from(event.target.files || []);
      const oversized = newFiles.filter(f => f.size > 10 * 1024 * 1024);
      if (oversized.length > 0) {
        swal.fire({
          title: this.$t("error"),
          text: oversized.map(f => f.name).join(", ") + ": " + this.$t("account.file_too_large"),
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false,
        });
        if (this.$refs.batchAttachFileInput) {
          this.$refs.batchAttachFileInput.value = "";
        }
        return;
      }
      if (!this.batchAttachRow.files) {
        this.$set(this.batchAttachRow, "files", []);
      }
      this.batchAttachRow.files = this.batchAttachRow.files.concat(newFiles);
      if (this.$refs.batchAttachFileInput) {
        this.$refs.batchAttachFileInput.value = "";
      }
    },
    removeBatchFile(index) {
      if (this.batchAttachRow && this.batchAttachRow.files) {
        this.batchAttachRow.files.splice(index, 1);
      }
    },
  },
};
</script>

<style>
.no-border-card .card-footer {
  border-top: 0;
}
</style>
